'use server'

import { apiRequest, ApiResponse } from '../request'

export interface ShippingConfig {
    id: number
    companyId: number
    senderZipCode?: string
    senderAddress?: {
        street?: string
        number?: string
        complement?: string
        neighborhood?: string
        city?: string
        state?: string
    }
    enabledCarriers?: string[]
    freeShippingThreshold?: number
    handlingTimeDays: number
    active: boolean
    /** Quem paga o frete de entrega. */
    paymentMode?: ShippingPaymentMode
    /** SUBSIDIZED: quanto a loja paga do frete (centavos). */
    storeSubsidyAmount?: number | null
    /** FLAT_RATE: valor fixo cobrado do cliente (centavos). */
    flatRateAmount?: number | null
    deliveryEnabled?: boolean
    pickupEnabled?: boolean
    /** Onde retirar; vazio usa o endereço de origem. */
    pickupAddress?: {
        street?: string
        number?: string
        complement?: string
        neighborhood?: string
        city?: string
        state?: string
        zipCode?: string
    } | null
    pickupInstructions?: string | null
    hasToken?: boolean
    createdAt: string
    updatedAt: string
}

export type ShippingPaymentMode = 'CUSTOMER' | 'STORE' | 'SUBSIDIZED' | 'FLAT_RATE'

export interface ShippingQuote {
    /** Quanto a transportadora cobra — o custo da etiqueta. */
    carrierPrice?: number
    carrier: string
    service: string
    price: number
    originalPrice?: number
    deliveryDays: number
    deliveryRange?: { min: number; max: number }
    freeShipping?: boolean
    error?: string
}

export interface ShippingCalculateResponse {
    quotes: ShippingQuote[]
    freeShippingApplied?: boolean
    error?: string
}

export async function getShippingConfigByCompany(
    companyId: number,
): Promise<ApiResponse<ShippingConfig>> {
    return apiRequest<ShippingConfig>(
        `shipping/config/company/${companyId}`,
        {},
    )
}

export async function getShippingConfigByStore(
    storeConfigId: number,
): Promise<ApiResponse<ShippingConfig>> {
    return apiRequest<ShippingConfig>(
        `shipping/config/store/${storeConfigId}`,
        {},
    )
}

/** Produto (ou variação) da loja sem peso ou sem medida: o frete usa o padrão. */
export interface ShippingDimensionGap {
    id: number
    /** O produto a editar (na variação, o pai). */
    productId: number
    name: string
    /** O que falta: "peso", "largura", "altura", "comprimento". */
    missing: string[]
}

export async function getShippingDimensionGaps(
    storeConfigId: number,
): Promise<ApiResponse<ShippingDimensionGap[]>> {
    return apiRequest<ShippingDimensionGap[]>(
        `shipping/config/store/${storeConfigId}/dimension-gaps`,
        {},
    )
}

export async function createShippingConfig(
    data: Partial<ShippingConfig>,
): Promise<ApiResponse<ShippingConfig>> {
    return apiRequest<ShippingConfig>('shipping/config', { data }, 'post')
}

export async function updateShippingConfig(
    id: number,
    data: Partial<ShippingConfig>,
): Promise<ApiResponse<ShippingConfig>> {
    return apiRequest<ShippingConfig>(
        `shipping/config/${id}`,
        { data },
        'patch',
    )
}

export async function deleteShippingConfig(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`shipping/config/${id}`, {}, 'delete')
}

export async function calculateShipping(data: {
    companyId: number
    /** Loja cuja configuração de frete simular. */
    storeConfigId?: number
    toZipCode: string
    items: {
        width: number
        height: number
        depth: number
        weight: number
        quantity: number
        insuranceValue: number
    }[]
    orderTotal?: number
}): Promise<ApiResponse<ShippingCalculateResponse>> {
    return apiRequest<ShippingCalculateResponse>(
        'shipping/calculate',
        { data },
        'post',
    )
}

export async function authenticateMelhorEnvio(data: {
    code: string
    companyId: number
}): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        'shipping/melhor-envio/auth',
        { data },
        'post',
    )
}

/** `storeId`: o retorno do OAuth volta para a tela de frete desta loja. */
export async function getMelhorEnvioAuthUrl(
    companyId: number,
    storeId?: number,
): Promise<ApiResponse<{ url: string }>> {
    return apiRequest<{ url: string }>(
        `shipping/melhor-envio/authorize/${companyId}`,
        { params: storeId ? { storeId } : {} },
    )
}
